import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ShoppingCart,
  Warehouse,
  Settings,
  Info,
  Users,
  ChevronDown,
  LogOut,
  Package,
  FileText,
  Upload,
  Send,
  CheckCircle,
  Truck,
  FileCheck,
  AlertCircle,
  Download,
  ClipboardCheck,
  PlusCircle,
  Edit,
  UserPlus,
  UserCog,
  ChevronLeft,
  ChevronRight,
  User,
  TrendingUp,
  DollarSign,
  ShieldAlert,
  ScanBarcode,
  Layers,
  GitBranch,
  BoxSelect,
  Globe,
  FilePlus,
  GitFork,
  Barcode,
  CircleUser,
  Store,
  Mails,
  Tag,
  ArrowLeftRight
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar
} from "src/shadcn/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "src/shadcn/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "src/shadcn/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "src/shadcn/ui/avatar";
import { Button } from "src/shadcn/ui/button";
import { TooltipProvider } from "src/shadcn/ui/tooltip";
import { NavItem } from "src/types";
import healfLogo from "src/assets/images/icons/healf-ops.svg";
import infoIcon from "src/assets/images/logos/h-logo.png";
import { useAuthorisation, useAuthentication } from "src/context";

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  navItems: NavItem[];
  onLogout: () => void;
}

const iconMap: Record<string, React.ReactNode> = {
  // Purchase Order
  "Purchase Orders": <Package className="h-4 w-4" />,
  "Transit Purchase Orders": <Truck className="h-4 w-4" />,
  "Sub Purchase Orders": <Globe className="h-4 w-4" />,
  "Transfer Orders": <ArrowLeftRight className="h-4 w-4" />,
  "AA Purchase Orders": <Package className="h-4 w-4" />,
  "AA Warehouse Check-Ins": <ClipboardCheck className="h-4 w-4" />,
  "AA Goods-in Exports": <Download className="h-4 w-4" />,
  Create: <FilePlus className="h-4 w-4" />,
  "Create Child PO": <GitFork className="h-4 w-4" />,
  Upload: <Upload className="h-4 w-4" />,
  Send: <Send className="h-4 w-4" />,
  "Update Confirmation": <FileCheck className="h-4 w-4" />,
  Amend: <Edit className="h-4 w-4" />,
  "Review Confirmation": <CheckCircle className="h-4 w-4" />,
  "Update Shipment": <Truck className="h-4 w-4" />,
  "Update Invoice": <FileText className="h-4 w-4" />,
  "Resolve GXO Error": <AlertCircle className="h-4 w-4" />,
  "Invoice Exports": <Download className="h-4 w-4" />,
  "HMRC Exchange Rates": <TrendingUp className="h-4 w-4" />,

  // Warehouse
  "Warehouse Check-ins": <ClipboardCheck className="h-4 w-4" />,
  "Check-in Purchase Order": <Package className="h-4 w-4" />,
  "Check-in Unknown Arrival": <AlertCircle className="h-4 w-4" />,
  "Goods-in Exports": <Download className="h-4 w-4" />,
  "PO Barcode Lookup": <Barcode className="h-4 w-4" />,

  // Claims
  "Claims Dashboard": <ClipboardCheck className="h-4 w-4" />,
  "Create Claim": <PlusCircle className="h-4 w-4" />,

  // Management
  "User Accounts": <CircleUser className="h-4 w-4" />,
  Vendors: <Store className="h-4 w-4" />,
  "Bulk Email to Vendors": <Mails className="h-4 w-4" />,
  "Create Vendor": <PlusCircle className="h-4 w-4" />,
  "Update Vendor": <Edit className="h-4 w-4" />,
  "Create User Account": <UserPlus className="h-4 w-4" />,
  "Update User Account": <UserCog className="h-4 w-4" />,

  // Relabelling
  "EU Relabel Check-In": <ScanBarcode className="h-4 w-4" />,
  "Relabel Case Builder": <BoxSelect className="h-4 w-4" />,
  "Relabel Case Tracking": <GitBranch className="h-4 w-4" />,
  "Pallet Builder": <Layers className="h-4 w-4" />,
  "Shipment Manager": <Truck className="h-4 w-4" />,

  // Standalone items
  "Brand Submissions": <Tag className="h-4 w-4" />,
  Settings: <Settings className="h-4 w-4" />,
  Info: <Info className="h-4 w-4" />
};

// Group icon mapping
const groupIconMap: Record<string, React.ReactNode> = {
  "Purchase Order": <ShoppingCart className="h-5 w-5" />,
  "Active Ants": <Package className="h-5 w-5" />,
  Finance: <DollarSign className="h-5 w-5" />,
  Warehouse: <Warehouse className="h-5 w-5" />,
  Claims: <ShieldAlert className="h-5 w-5" />,
  Management: <Users className="h-5 w-5" />,
  Relabelling: <Truck className="h-5 w-5" />
};

// Get initials from email
const getInitials = (email: string): string => {
  if (!email) return "U";
  const parts = email.split("@")[0].split(".");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return email.substring(0, 1).toUpperCase();
};

export function AppSidebar({ navItems, onLogout, ...props }: AppSidebarProps) {
  const location = useLocation();
  const { userRole } = useAuthorisation();
  const { userEmail } = useAuthentication();
  const { state, toggleSidebar } = useSidebar();

  const displayEmail = userEmail || "user@company.com";

  const isCollapsed = state === "collapsed";

  return (
    <TooltipProvider delayDuration={0}>
      <Sidebar collapsible="icon" className="border-r border-gray-800" {...props}>
        <SidebarHeader className={`border-b border-gray-800 relative ${isCollapsed ? "p-2" : "p-4"}`}>
          <Link to="/" className="flex items-center justify-center group-data-[collapsible=icon]:justify-center">
            <img
              src={isCollapsed ? infoIcon : healfLogo}
              alt="Healf"
              className={isCollapsed ? "h-6 w-auto" : "h-10 w-auto"}
            />
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="absolute -right-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full border border-gray-700 bg-gray-900 hover:bg-gray-800 z-[100] shadow-lg cursor-pointer"
            onMouseEnter={(e) => e.stopPropagation()}
            onMouseLeave={(e) => e.stopPropagation()}
          >
            {isCollapsed ? (
              <ChevronRight className="h-3 w-3 text-gray-400" />
            ) : (
              <ChevronLeft className="h-3 w-3 text-gray-400" />
            )}
          </Button>
        </SidebarHeader>

        <SidebarContent className="px-2 py-4 !overflow-y-auto">
          {navItems.map((item, index) => {
            // If item has children, render as collapsible group
            if (item.children && item.children.length > 0) {
              return (
                <Collapsible
                  key={index}
                  defaultOpen={item.children.some((child) => child.path === location.pathname)}
                  className="group/collapsible mb-2"
                >
                  <SidebarGroup className="p-0">
                    <SidebarGroupLabel asChild className="px-0">
                      <CollapsibleTrigger className="group flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-base font-semibold text-gray-300 transition-all hover:bg-gray-900 hover:text-white data-[state=open]:bg-gray-900 data-[state=open]:text-white group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:mt-0 group-data-[collapsible=icon]:opacity-100">
                        <div className="flex items-center gap-3 flex-1 group-data-[collapsible=icon]:flex-none group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0">
                          <span className="text-gray-400 group-hover:text-white group-data-[state=open]:text-white transition-colors shrink-0 flex items-center justify-center">
                            {groupIconMap[item.label] || <Package className="h-5 w-5" />}
                          </span>
                          <span className="font-semibold tracking-wide group-data-[collapsible=icon]:hidden">
                            {item.label}
                          </span>
                        </div>
                        <ChevronDown className="h-4 w-4 text-gray-500 transition-transform duration-300 group-data-[state=open]:rotate-180 group-data-[state=open]:text-white group-data-[collapsible=icon]:hidden shrink-0" />
                      </CollapsibleTrigger>
                    </SidebarGroupLabel>
                    <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                      <SidebarGroupContent className="mt-1">
                        <SidebarMenu>
                          {item.children.map((child, childIndex) => (
                            <SidebarMenuItem key={childIndex}>
                              <SidebarMenuButton
                                asChild
                                isActive={child.path === location.pathname}
                                tooltip={child.label}
                                className="my-0.5 group-data-[collapsible=icon]:ml-0 group-data-[collapsible=icon]:justify-center"
                              >
                                <Link
                                  to={child.path || "#"}
                                  className={`flex items-center gap-3 rounded-md px-3 py-2 text-[0.9375rem] transition-all group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:justify-center ${
                                    child.path === location.pathname
                                      ? "bg-gray-800 text-white font-medium shadow-sm"
                                      : "text-gray-400 hover:bg-gray-900/50 hover:text-gray-200"
                                  }`}
                                >
                                  <span
                                    className={`shrink-0 transition-colors ${
                                      child.path === location.pathname
                                        ? "text-white"
                                        : "text-gray-500 group-hover/item:text-gray-300"
                                    }`}
                                  >
                                    {iconMap[child.label] || <FileText className="h-4 w-4" />}
                                  </span>
                                  <span className="group-data-[collapsible=icon]:sr-only">{child.label}</span>
                                </Link>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          ))}
                        </SidebarMenu>
                      </SidebarGroupContent>
                    </CollapsibleContent>
                  </SidebarGroup>
                </Collapsible>
              );
            }

            // If item has no children, render as single menu item
            return (
              <SidebarGroup key={index} className="p-0 mb-1">
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={item.path === location.pathname}
                      tooltip={item.label}
                      className="group/item"
                    >
                      <Link
                        to={item.path || "#"}
                        className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-base font-medium transition-all group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-2 ${
                          item.path === location.pathname
                            ? "bg-gray-800 text-white shadow-sm"
                            : "text-gray-400 hover:bg-gray-900 hover:text-white"
                        }`}
                      >
                        <span
                          className={`shrink-0 transition-colors ${
                            item.path === location.pathname
                              ? "text-white"
                              : "text-gray-500 group-hover/item:text-gray-300"
                          }`}
                        >
                          {iconMap[item.label] || <Info className="h-5 w-5" />}
                        </span>
                        <span className="group-data-[collapsible=icon]:sr-only">{item.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroup>
            );
          })}
        </SidebarContent>

        <SidebarFooter className="border-t border-gray-800 p-2">
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    className="data-[state=open]:bg-gray-900 hover:bg-gray-900 transition-colors"
                  >
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg bg-gradient-to-br from-gray-700 to-gray-900 text-white font-semibold">
                        {getInitials(displayEmail)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold text-gray-200">{displayEmail}</span>
                      <span className="truncate text-xs text-gray-500">{userRole}</span>
                    </div>
                    <ChevronDown className="ml-auto h-4 w-4 text-gray-500" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg bg-gray-900 border-gray-800"
                  side="bottom"
                  align="end"
                  sideOffset={4}
                >
                  <DropdownMenuLabel className="p-0 font-normal">
                    <div className="flex items-center gap-2 px-2 py-2 text-left text-sm">
                      <Avatar className="h-8 w-8 rounded-lg">
                        <AvatarFallback className="rounded-lg bg-gradient-to-br from-gray-700 to-gray-900 text-white font-semibold">
                          {getInitials(displayEmail)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="grid flex-1 text-left text-sm leading-tight">
                        <span className="truncate font-semibold text-gray-200">{displayEmail}</span>
                        <span className="truncate text-xs text-gray-500">{userRole}</span>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-gray-800" />
                  <DropdownMenuItem
                    asChild
                    className="gap-2 text-gray-300 hover:text-white focus:bg-gray-800 focus:text-white cursor-pointer"
                  >
                    <Link to="/profile" className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <span>Profile</span>
                    </Link>
                  </DropdownMenuItem>
                  {/* <DropdownMenuItem className="gap-2 text-gray-300 hover:text-white focus:bg-gray-800 focus:text-white cursor-pointer">
                    <Settings className="h-4 w-4" />
                    <span>Settings</span>
                  </DropdownMenuItem> */}
                  <DropdownMenuSeparator className="bg-gray-800" />
                  <DropdownMenuItem
                    onClick={onLogout}
                    className="gap-2 text-red-400 hover:text-red-300 focus:bg-red-950/30 focus:text-red-300 cursor-pointer"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
    </TooltipProvider>
  );
}
